import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { X, CircleNotch, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import { shareConversation } from 'api/companyMapping';
import { useFeedback } from 'contexts/feedback';

export default function SharePopup({ visible, setVisible }) {
    const { conversationId } = useParams();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const { setError: setFeedbackError } = useFeedback();

    const { mutate: share, status } = useMutation({
        mutationKey: ['shareCompanyMapping', conversationId],
        mutationFn: shareConversation,
        onSuccess: () => {
            setSuccess(true);
        },
        onError: () => {
            setFeedbackError('Errore durante l\'invio. Riprova più tardi.');
        }
    });

    const isPending = status === 'pending';

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (!email.trim()) {
            setError('Inserisci un indirizzo email');
            return;
        }

        if (!validateEmail(email)) {
            setError('Inserisci un indirizzo email valido');
            return;
        }

        share({
            conversationId,
            body: { email }
        });
    };

    const handleClose = () => {
        if (isPending) return;
        setVisible(false);
        setEmail('');
        setError('');
        setSuccess(false);
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                onClick={handleClose}
            />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-black/10">
                    <h2 className="text-lg font-semibold text-black/90">
                        Esporta Mappatura
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-black/5 transition-colors"
                        disabled={isPending}
                    >
                        <X size={20} weight="bold" className="text-black/50" />
                    </button>
                </div>

                {success ? (
                    <div className="p-6 text-center">
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle size={32} weight="fill" className="text-emerald-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-black/90 mb-2">
                            Email inviata!
                        </h3>
                        <p className="text-black/60 mb-6">
                            Abbiamo inviato il link per scaricare l&apos;esportazione a <span className="font-medium text-black/80">{email}</span>
                        </p>
                        <button
                            onClick={handleClose}
                            className="w-full px-4 py-3 rounded-xl bg-black text-white font-medium hover:bg-black/90 transition-colors"
                        >
                            Chiudi
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-black/5 rounded-full flex items-center justify-center">
                                <EnvelopeSimple size={20} className="text-black/50" />
                            </div>
                            <p className="text-black/60 text-sm">
                                Inserisci il tuo indirizzo email per ricevere il link di esportazione della mappatura.
                            </p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-black/70 mb-2">
                                Indirizzo Email
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError('');
                                }}
                                placeholder="nome@esempio.com"
                                disabled={isPending}
                                className={`w-full px-4 py-3 rounded-xl border ${
                                    error ? 'border-red-500' : 'border-black/10'
                                } focus:outline-none focus:border-black/30 transition-colors disabled:opacity-50`}
                                autoFocus
                            />
                            {error && (
                                <p className="text-red-500 text-sm mt-1">{error}</p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isPending}
                                className="flex-1 px-4 py-3 rounded-xl border border-black/10 text-black/70 font-medium hover:bg-black/5 transition-colors disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                disabled={isPending}
                                className="flex-1 px-4 py-3 rounded-xl bg-black text-white font-medium hover:bg-black/90 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                            >
                                {isPending && <CircleNotch size={18} className="animate-spin" />}
                                Invia
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
